/**
 * Section 20a — Terms of Service (PRE_LAUNCH CL1 / F4).
 *
 * IMPORTANT — These terms are a working draft authored from general
 * knowledge of Indian contract / consumer law and SEBI framing. They are
 * NOT a substitute for review by qualified counsel. Before unrestricted
 * public launch in India have a SEBI-aware advocate review and finalise.
 * Version pinned by backend/src/config/legal.ts LEGAL_VERSION.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import GuideCallout from '../GuideCallout';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 3.5, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);
const L = ({ children }: { children: React.ReactNode }) => (
  <Box component="ul" sx={{ pl: 3, mb: 1.5 }}>{children}</Box>
);
const Li = ({ children }: { children: React.ReactNode }) => (
  <Box component="li" sx={{ mb: 0.5, fontSize: '0.95rem', lineHeight: 1.6 }}>{children}</Box>
);

const Section_terms: GuideSection = {
  id: 'terms',
  label: 'Terms of Service',
  group: 'help',
  icon: <DescriptionIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>Terms of Service</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Effective: 2026-05-30 · Version 2026.05 · Governing law: India
      </Typography>

      <GuideCallout kind="warning" title="Draft pending counsel review">
        These Terms have been prepared from general knowledge of Indian contract,
        consumer, and securities-related law and are <strong>not yet attorney-vetted</strong>.
        Treat them as an interim agreement; the operator commits to publishing a
        counsel-reviewed version before onboarding unrelated paying users.
      </GuideCallout>

      <H id="parties">1. Parties &amp; acceptance</H>
      <P>
        These Terms of Service ("Terms") constitute a binding agreement between you,
        the individual accessing or using the earngush application at
        <strong> https://earngush.com</strong> (the "Platform"), and the operator of
        the Platform ("we", "us", "Operator"). By signing in to the Platform — or by
        clicking the "I have read and agree" button on the legal-acceptance gate — you
        confirm that you have read, understood, and agreed to be bound by these Terms,
        the <em>Privacy Policy</em>, and the <em>Risk Disclaimer</em>.
      </P>
      <P>
        If you do not agree, do not use the Platform. Continued use after a material
        revision of these Terms (signalled by a version bump on the legal-acceptance
        gate) constitutes acceptance of the revised Terms.
      </P>

      <H id="eligibility">2. Eligibility</H>
      <L>
        <Li>You must be at least 18 years of age and competent to contract under the Indian Contract Act, 1872.</Li>
        <Li>You must be lawfully entitled to open and operate the broker accounts you connect to the Platform (NSE / BSE / MCX / Delta Exchange).</Li>
        <Li>You must not be a person against whom an order has been passed by SEBI, RBI, or any other Indian regulator barring participation in securities markets.</Li>
        <Li>You must not be a resident of, or accessing the Platform from, any jurisdiction in which the use of cryptocurrency-derivative platforms (such as Delta Exchange) is prohibited by local law.</Li>
      </L>

      <H id="account">3. Account, security, and use</H>
      <L>
        <Li>
          You are solely responsible for maintaining the confidentiality of your sign-in
          credentials, broker API keys, webhook secrets, and any two-factor authentication
          codes. Notify the Operator immediately if you suspect unauthorised access.
        </Li>
        <Li>
          You will not share your account with any other person, will not create multiple
          accounts to evade controls, and will not attempt to access another user's data.
        </Li>
        <Li>
          You will use the Platform only for your own personal account management. Operating
          the Platform as an investment-advisory or portfolio-management service for third
          parties is not permitted unless you are appropriately registered with SEBI and have
          obtained the Operator's written consent.
        </Li>
        <Li>
          You will not reverse-engineer, scrape at abusive rates, or otherwise interfere
          with the integrity, security, or performance of the Platform.
        </Li>
      </L>

      <H id="not-advice">4. Not investment advice — no fiduciary relationship</H>
      <P>
        The Operator is <strong>not a SEBI-registered Investment Adviser</strong>, Research
        Analyst, Portfolio Manager, or Stockbroker. Nothing on the Platform — including
        signals emitted by the in-house Sniper, RockBand, or DailyBlast scanners, any
        chart indicator, any back-test result, any alert, the App Guide, or any
        communication from the Operator — constitutes investment, tax, accounting, or
        legal advice, or a solicitation to buy or sell any security, derivative,
        commodity, or cryptocurrency.
      </P>
      <P>
        The Platform is a self-directed tool for technical analysis and order routing.
        Every decision to enter, exit, modify, or refuse a trade is yours alone. You
        agree to consult a SEBI-registered adviser or other qualified professional
        before making investment decisions if you are unsure.
      </P>

      <H id="broker-link">5. Broker integrations — you are the principal</H>
      <P>
        The Platform connects to third-party brokers (Zerodha, Delta Exchange, and others
        that may be added) using API keys and secrets that <em>you</em> supply. When
        the Platform places, modifies, or cancels an order, it is doing so as your
        authorised technical agent. The legal relationship for the trade itself is
        between you and the broker / exchange. The Operator is not a counterparty,
        custodian, clearing house, or broker-dealer for any trade.
      </P>
      <P>
        You agree that the Operator's role is limited to (i) transmitting your
        instructions to the broker as you have configured them and (ii) presenting
        broker-supplied data back to you. The Operator does not guarantee execution,
        latency, fills at any specific price, or any specific outcome.
      </P>

      <H id="auto-trade">6. Automated trading &amp; webhooks</H>
      <P>
        The Platform offers automated order placement triggered by TradingView webhooks,
        internal scanners, and timed jobs. Automation is opt-in. By enabling
        Auto-Trade you authorise the Platform to place real orders against your live
        broker account without further per-order confirmation, subject only to the
        risk-management controls you have configured (per-user daily-loss cap, kill
        switch, per-symbol Auto Long / Auto Short toggles, position sizing rules).
      </P>
      <P>
        You acknowledge that:
      </P>
      <L>
        <Li>Automation can fail, partially fail, or fire unintended orders due to bugs, network issues, broker outages, exchange halts, or upstream data feed corruption.</Li>
        <Li>You must monitor your positions independently and must be prepared to disable Auto-Trade and intervene manually at any time.</Li>
        <Li>You bear all financial consequences — gains and losses — of orders placed via automation, even if those orders resulted from a Platform malfunction.</Li>
      </L>

      <H id="paper-trade">7. Simulated and modelled results</H>
      <P>
        All orders placed through the Platform — manually or via Auto-Trade — are sent to
        your live broker account; there is no paper / simulator trading mode. Any
        back-tested results, simulated equity curves, replay output, or modelled
        performance shown on the Platform are <strong>hypothetical</strong> and do not
        reflect actual execution. Real-world results differ due to slippage, fees,
        partial fills, latency, and the well-documented difference between in-sample and
        out-of-sample performance.
      </P>

      <H id="fees">8. Fees, broker charges, and taxes</H>
      <P>
        Use of the Platform may, at the Operator's option and on prior notice, be
        subject to subscription or usage fees. As of the effective date no such fees
        are charged.
      </P>
      <P>
        Broker brokerage, exchange fees, transaction charges, SEBI fees, stamp duty,
        Securities Transaction Tax (STT) and Commodities Transaction Tax (CTT), GST on
        applicable services, depository charges, and all other statutory levies are
        the user's responsibility and are deducted by the broker directly. Income-tax
        treatment of trading gains and losses is governed by the Income-tax Act, 1961
        and is the user's responsibility.
      </P>

      <H id="availability">9. Availability — no uptime guarantee</H>
      <P>
        The Platform is provided on a best-effort basis. The Operator does not
        guarantee uninterrupted availability, any specific response time, or freedom
        from defects. Maintenance windows, hosting-provider outages (Contabo,
        upstream connectivity), broker API outages, and exchange halts will cause
        downtime. You accept that downtime may occur during active positions and
        that you remain responsible for managing positions through alternative means
        (broker terminal, mobile app, telephone) during such windows.
      </P>

      <H id="indemnity">10. Limitation of liability &amp; indemnity</H>
      <P>
        To the maximum extent permitted by applicable law:
      </P>
      <L>
        <Li>
          <strong>The Operator is not liable</strong> for trading losses, missed
          opportunities, indirect losses, consequential losses, loss of profits, loss
          of data, or loss of goodwill arising out of or in connection with your use
          of the Platform, whether the claim arises in contract, tort (including
          negligence), or otherwise.
        </Li>
        <Li>
          Where liability cannot be fully excluded by law (e.g. for fraud or
          intentional misconduct), the Operator's aggregate liability to you in any
          twelve-month period is limited to the greater of (a) the fees you have
          actually paid to the Operator in that period or (b) INR 5,000.
        </Li>
        <Li>
          You agree to indemnify and hold the Operator harmless against any claim,
          demand, regulatory action, or loss (including reasonable legal fees)
          arising from (i) your breach of these Terms, (ii) your violation of any
          applicable law including SEBI, RBI, IT Act, and tax law, or (iii) any
          third-party claim that your use of the Platform infringed their rights.
        </Li>
      </L>

      <H id="termination">11. Suspension &amp; termination</H>
      <P>
        The Operator may suspend or terminate your access without notice if (a) you
        breach these Terms, (b) the Operator reasonably suspects fraudulent or
        unauthorised activity on your account, (c) a regulator directs the Operator
        to do so, or (d) continued provision becomes commercially or technically
        infeasible. You may close your account at any time from Settings; data
        retention on closure is described in the <em>Privacy Policy</em>.
      </P>

      <H id="changes">12. Changes to these Terms</H>
      <P>
        The Operator may revise these Terms. Material revisions will be communicated
        by bumping the legal version constant, which causes the in-app
        legal-acceptance gate to re-appear for every existing user on next sign-in.
        Continued use after acceptance constitutes agreement to the revised Terms.
        Trivial revisions (typo, formatting) do not trigger a re-gate.
      </P>

      <H id="law">13. Governing law &amp; dispute resolution</H>
      <P>
        These Terms are governed by the laws of India without regard to conflict-of-law
        principles. Any dispute, controversy, or claim arising out of or relating to
        these Terms or the Platform shall be referred to and finally resolved by
        arbitration in <strong>Bengaluru, Karnataka, India</strong> under the
        Arbitration and Conciliation Act, 1996, by a sole arbitrator nominated by
        mutual agreement (or, failing agreement within 30 days, by the procedure set
        out in that Act). The seat of arbitration is Bengaluru, the language is
        English, and the award is final and binding. Notwithstanding the foregoing,
        either party may seek interim relief from the courts at Bengaluru.
      </P>

      <H id="contact">14. Contact</H>
      <P>
        For notices, requests, or grievances under these Terms or under the
        Information Technology Act, 2000 (Sec. 79) / IT (Intermediary Guidelines and
        Digital Media Ethics Code) Rules, 2021, write to the Operator at the contact
        address published on the Platform's home page or in your account settings.
        The Operator will acknowledge complaints within forty-eight (48) hours and
        endeavour to resolve them within fifteen (15) days.
      </P>
    </>
  ),
};

export default Section_terms;
